/**
 * Pauses a running guided tour while a MODAL is open, and resumes it (same step)
 * when the modal closes.
 *
 * Why: joyride renders at its library default (dimmer z=100, tour card z=101)
 * while the app's modal family (Dialog / Sheet / AlertDialog) renders at z-50 —
 * so a dialog opened mid-tour (e.g. by clicking a spotlit "add" button) opens
 * UNDERNEATH the tour's dimmer and card, still focus-trapping and scroll-locking,
 * which reads as a frozen page. Rather than out-stack the modals (a z-ladder
 * redesign with a large regression surface), the tour YIELDS: its portal is
 * hidden while any modal is open and restored when the last one closes.
 *
 * Scope: MODAL surfaces only. Popover / Select / Dropdown / Tooltip already
 * stack above the tour (z-150/z-200 — see components/ui/select.tsx) and open
 * transiently from spotlit controls; pausing on those would flicker the tour
 * on every dropdown. Their data-slots are deliberately NOT in the selector.
 *
 * Mechanism: hiding via `visibility` (not unmount) keeps joyride's step state
 * machine and floating-ui autoUpdate alive, so "resume on the same step" is
 * automatic. On resume we dispatch window "resize" — joyride's
 * useTargetPosition listens for it and recomputes the spotlight rect — because
 * whatever the modal did (created a row, renamed a card) may have moved the
 * anchor while the tour was hidden. Same signal the reveal-settle logic uses.
 */

import { useEffect, useRef, useState, type RefObject } from "react";

/** Open MODAL-family content, all portaled to <body> by Radix. The data-slot
 * markers on Dialog/AlertDialog content exist for this selector (see the
 * comments in components/ui/dialog.tsx / alert-dialog.tsx); sheet-content is
 * stock shadcn. `data-state="open"` excludes content kept mounted during the
 * close animation, so the tour reappears the moment the close starts. */
export const MODAL_CONTENT_SELECTOR = [
  '[data-slot="dialog-content"][data-state="open"]',
  '[data-slot="alert-dialog-content"][data-state="open"]',
  '[data-slot="sheet-content"][data-state="open"]',
].join(", ");

/** The element react-joyride portals its overlay + floater into (PORTAL_ELEMENT_ID). */
export const JOYRIDE_PORTAL_ID = "react-joyride-portal";

/** True while any modal (dialog / sheet / alert-dialog) is open. Pure — reads
 * only from the given document. Exported for unit testing. */
export function isModalOpen(doc: Document): boolean {
  return doc.querySelector(MODAL_CONTENT_SELECTOR) !== null;
}

export interface ModalPauseOptions {
  /** Called INSTEAD of the resume-remeasure when the modal closes on a
   * DIFFERENT route than it opened on — i.e. an action inside the modal
   * deliberately navigated away (e.g. the create-standard dialog's "Open Kory
   * chat"). The tour must not yank the user back to the step's route, so the
   * owner should end it (without any exit navigation of its own). */
  onResumeElsewhere?: () => void;
}

/**
 * Watches the DOM for open modals and hides/restores the joyride portal
 * accordingly. Mount alongside a running tour (TourRunner).
 *
 * `pausedRef` is the engine-side contract: while `.current` is true, the step
 * target function must take no ACTIONS (no route navigation, no reveal-trigger
 * clicks) — otherwise the still-running engine fights what the user does inside
 * the modal (a navigation from a modal action gets instantly reverted by the
 * step's own navigate-to-route logic). It stays true through a resume-elsewhere
 * so the engine can't navigate in the gap before the owner's stop() lands.
 */
export function useModalPause(opts: ModalPauseOptions = {}): {
  modalOpen: boolean;
  pausedRef: RefObject<boolean>;
} {
  const [modalOpen, setModalOpen] = useState(() => isModalOpen(document));
  const pausedRef = useRef(modalOpen);
  // Route the pause began on — a different route at close means an action
  // inside the modal navigated, and resuming in place would be wrong.
  // Captured synchronously with detecting the open (here for a modal already
  // open at mount, in the observer otherwise): the resume effect runs a
  // scheduler flush later, and a navigation landing in that gap would be
  // misread as the route the pause began on.
  const pausePathnameRef = useRef<string | null>(
    modalOpen ? window.location.pathname : null,
  );
  // The close-time verdict (route changed while paused?), also decided
  // synchronously in the observer. Both refs live and die with the DOM's
  // open/close transitions, never with React commits — a rapid open→close
  // that React batches into a single no-op commit must not leave a stale
  // route behind for a later pause to compare against.
  const resumeElsewhereRef = useRef(false);
  const onResumeElsewhereRef = useRef(opts.onResumeElsewhere);
  onResumeElsewhereRef.current = opts.onResumeElsewhere;
  // Distinguishes "resuming after a pause" from the initial mount, so the
  // remeasure resize only fires on a genuine modal close.
  const wasPausedRef = useRef(false);

  useEffect(() => {
    // Radix mounts/unmounts modal content as direct <body> portals and flips
    // data-state during close animations — childList + the attribute filter
    // cover both. setState with an unchanged boolean is a no-op re-render-wise,
    // so recomputing on unrelated mutations is cheap.
    const observer = new MutationObserver(() => {
      const open = isModalOpen(document);
      if (open && pausePathnameRef.current == null) {
        pausePathnameRef.current = window.location.pathname;
      } else if (!open && pausePathnameRef.current != null) {
        resumeElsewhereRef.current =
          pausePathnameRef.current !== window.location.pathname;
        pausePathnameRef.current = null;
        // When React batched the whole pause away (open→close in one commit),
        // the hide-effect below never ran and its restore-cleanup never will —
        // restore here so the shared portal can't stay hidden for the next
        // tour. Idempotent with that cleanup when the pause did commit.
        const portal = document.getElementById(JOYRIDE_PORTAL_ID);
        if (portal) portal.style.visibility = "";
      }
      setModalOpen(open);
      // Joyride creates its portal lazily — when a tour starts OVER an
      // already-open modal, the pause effect below ran before the portal
      // existed and won't re-run (modalOpen never transitions). The portal's
      // creation is itself a body mutation, so hide it from here.
      if (open) {
        const portal = document.getElementById(JOYRIDE_PORTAL_ID);
        if (portal) portal.style.visibility = "hidden";
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-state"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const portal = document.getElementById(JOYRIDE_PORTAL_ID);
    if (modalOpen) {
      wasPausedRef.current = true;
      pausedRef.current = true;
      if (portal) portal.style.visibility = "hidden";
      return () => {
        // Restore on modal close AND on tour unmount mid-pause — the portal
        // element is shared across tours, so leaving it hidden would blank
        // the next tour.
        const p = document.getElementById(JOYRIDE_PORTAL_ID);
        if (p) p.style.visibility = "";
      };
    }
    if (!wasPausedRef.current) return;
    wasPausedRef.current = false;
    if (resumeElsewhereRef.current) {
      // An action inside the modal navigated away (the modal's copy may even
      // promise it — "You'll leave this screen"). Resuming would fight that:
      // the step's target would navigate straight back. Hand the decision to
      // the owner; pausedRef stays true so the engine stays inert until the
      // owner's stop() unmounts the tour.
      onResumeElsewhereRef.current?.();
      return;
    }
    pausedRef.current = false;
    // Resuming in place: the anchor may have drifted while hidden. One sync
    // dispatch covers layout that already settled; a second one rAF later
    // catches content that finishes painting asynchronously (same pattern as
    // the reveal settle-watcher in use-guided-overlay).
    window.dispatchEvent(new Event("resize"));
    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(raf);
  }, [modalOpen]);

  return { modalOpen, pausedRef };
}
